import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import useApi from 'lib/effects/api'
import { getDateFromParts } from 'lib/formik/dates'
import { formatDate } from 'lib/formatting/dates'
import { Grid, P, H } from '@farewill/ui'

import {
  getFullName,
  getContactSentence,
  getOriginalCallerContactIfNotOnEstate,
  StyledContactSentenceButton,
} from './helpers'
import { ProbateCaseAttributes } from 'lib/models/probate-case'
import {
  ProbateEstatePerson,
  ProbateEstate,
  ProbateEstatePeopleAttributes,
} from 'lib/models/probate-estate'
import { Lead } from 'lib/models/lead'
import { EstateForm } from './estate-form'

const CaseDetailsSummary = ({
  probateCase,
}: {
  probateCase: ProbateCaseAttributes
}): React.ReactElement => {
  const history = useHistory()
  const dispatch = useDispatch()
  const lead = useSelector((state: { lead: Lead }) => state.lead)
  const caseScore = lead.attributes?.caseScore

  const [
    { isLoading: isProbateEstatePeopleLoading, data: probateEstatePeople },
    getProbateEstatePeople,
  ] = useApi<ProbateEstatePerson[]>({ data: [] })

  const [
    { isLoading: isProbateEstateLoading, data: probateEstate },
    fetchProbateEstate,
  ] = useApi<ProbateEstate>({ isLoading: true })

  const dateOfDeath = probateEstate
    ? formatDate(
        getDateFromParts({
          prefix: 'death',
          values: probateEstate.attributes,
        })
      ) || 'Not added'
    : undefined

  useEffect(() => {
    getProbateEstatePeople({
      url: `/api/probate-estates/${probateCase.estate.id}/people`,
    })
  }, [getProbateEstatePeople, probateCase.estate.id])

  useEffect(() => {
    fetchProbateEstate({
      url: `/api/probate-estates/${probateCase.estate.id}`,
    })
  }, [fetchProbateEstate, probateCase.estate.id])

  const callerNoLongerOnEstate = useMemo(
    () =>
      getOriginalCallerContactIfNotOnEstate(lead, probateEstatePeople || []),
    [lead, probateEstatePeople]
  )

  const estatePeopleSentences = useMemo(
    () =>
      (probateEstatePeople || [])
        .sort(
          (a, b) =>
            Number(b.attributes.isCustomer) - Number(a.attributes.isCustomer)
        )
        .map((person) =>
          getContactSentence(
            dispatch,
            person.attributes as ProbateEstatePeopleAttributes,
            probateCase.deceasedFirstName
          )
        )
        .filter((sentence) => sentence.length > 0),
    [probateEstatePeople, dispatch, probateCase.deceasedFirstName]
  )

  return (
    <>
      {(probateCase.deceasedFirstName || probateCase.deceasedLastName) && (
        <Grid.Item>
          <P margin={[0, 0, 'XS']}>
            Deceased name:{' '}
            <strong>
              {getFullName(
                probateCase.deceasedFirstName,
                probateCase.deceasedLastName
              )}
            </strong>
            .
          </P>
          <P>
            Date of death:{' '}
            {isProbateEstateLoading ? (
              'Loading estate...'
            ) : (
              <strong>{dateOfDeath}</strong>
            )}
          </P>
        </Grid.Item>
      )}
      <Grid.Item>
        <P margin={[0, 0, 'XS']}>
          Product:{' '}
          <strong>
            {probateCase.estateAdministrationRequired
              ? 'Complete Probate'
              : 'Essential Probate'}
          </strong>
        </P>
        <P margin={[0, 0, 'XS']}>
          Tax form:{' '}
          <strong>{probateCase.taxForm?.toUpperCase() || 'Not added'}</strong>
        </P>
        <P>
          Case score:{' '}
          {isProbateEstateLoading ? (
            'Loading estate...'
          ) : (
            <strong>{caseScore || 'Not added'}</strong>
          )}
        </P>
      </Grid.Item>
      <Grid.Item>
        {probateEstate && <EstateForm probateEstate={probateEstate} />}
      </Grid.Item>
      <Grid.Item>
        <H size="S">Contacts</H>
      </Grid.Item>
      <Grid.Item>
        {callerNoLongerOnEstate && (
          <P>
            Original caller:{' '}
            <StyledContactSentenceButton
              onClick={() =>
                history.push(`/contacts/${callerNoLongerOnEstate.id}/edit`)
              }
            >
              {callerNoLongerOnEstate.firstName}{' '}
              {callerNoLongerOnEstate.lastName}
            </StyledContactSentenceButton>
          </P>
        )}
        {isProbateEstatePeopleLoading ? (
          <P size="S">Loading contacts...</P>
        ) : estatePeopleSentences.length > 0 ? (
          estatePeopleSentences.map((sentence, index) => {
            const isLast = index === estatePeopleSentences.length - 1
            return (
              <P key={index} margin={[0, 0, isLast ? 0 : 'XS']}>
                {sentence.map((sentencePart, partIndex) => (
                  <span key={partIndex}>{sentencePart}</span>
                ))}
              </P>
            )
          })
        ) : (
          <p>No contacts added</p>
        )}
      </Grid.Item>
    </>
  )
}

export default CaseDetailsSummary
